/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { srOnly } from '@/components/blocks/Common/styles/commonStyles';
import { Linkable } from '@/components/blocks/Linkable';
import { TableCellProductImage } from '@/components/blocks/ProductImage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { OrderItemAttributeDrawer } from '@/components/content/OrderItemTable/parts/AttributeDrawer';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import { OrderItemUnitPrice } from '@/components/content/OrderItemTable/parts/UnitPrice';
import { orderItemTableItemDetailsImageSX } from '@/components/content/OrderItemTable/styles/orderItemTableItemDetailsImage';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTable';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { ReleaseDateFormat } from '@/utils/Date';
import { Stack, Typography } from '@mui/material';
import { FC, useContext } from 'react';

export const OrderItemItemDetails: FC = () => {
	const { details, isFreeItem, page } = useContext(ContentContext) as OrderItemTableRowData &
		ReturnType<typeof useOrderItemTableRow> 
		& {
			isFreeItem : boolean,
			page?:string
		};
	const { partNumber, name, thumbnail, href, prices, attributes, loading, releaseDate } = details;
	const orderItemTableNLS = useLocalization('OrderItemTable');
	const labels = useLocalization('OrderItemTable').Labels;
	const freeGiftNLS = useLocalization('FreeGift');
	const eaaMsgsNLS = useLocalization('EaaMessages');
	
	//const nameShort = name.length > 20 ? name.substring(0, 20) + '...' : name;
	const partNumberShort = partNumber.length > 20 ? partNumber.substring(0, 20) + '...' : partNumber;

	if (loading) {
		return <ProgressIndicator />;
	}
	
	return (
		<>
			<Stack direction="row" alignItems="flex-start" spacing={2} mb={isFreeItem && page==='shipping' ? 4.5 : 0}>
				{thumbnail ? (
					<Linkable href={href} id={href} data-testid={href}>
						<TableCellProductImage
							{...{
								src: thumbnail,
								alt: labels.ProductThumbnail.t(),
								isThumbnail: true,
								sx: orderItemTableItemDetailsImageSX,
							}}
						/>
					</Linkable>
				) : null}
				<Stack direction="column" alignItems="flex-start">
					<Linkable href={href} id={href} data-testid={href}>
						<Typography variant="h6" data-testid="orderItem-name" id="orderItem-name" className="orderItem-name">
							{name}
						</Typography>
					</Linkable>
					<Typography sx={srOnly}>{eaaMsgsNLS.productLinkPrefix.t()+" "+name}</Typography>
					{releaseDate && 
						<Typography color="#368632" fontSize={14}>
							{orderItemTableNLS.Msgs.ReleaseDate.t({releaseDate:ReleaseDateFormat(releaseDate)})}
						</Typography>
					}
					<Typography id="orderItem-partNumber" data-testid="orderItem-partNumber">
						{partNumberShort}
					</Typography>
					{prices ? <OrderItemUnitPrice /> : null}
					{attributes?.length ? <OrderItemAttributeDrawer attributes={attributes} /> : null}
					{isFreeItem ? <Typography variant="body1">{freeGiftNLS.Label.t()}</Typography> : null}
				</Stack>
			</Stack>
			{isFreeItem && page==='shipping' ? 
				<Typography sx={{position:'absolute', bottom:0}}>
					{freeGiftNLS.RemoveFreeGiftFromCart.t()}
				</Typography> 
			: null}
		</>
	);
};
